import { Controller } from "@hotwired/stimulus"
import {
  isLessonReleased,
  lessonVideos,
  renderVideoInto,
} from "../../helpers/lesson_media"

// data-controller="lessons--videos"
export default class extends Controller {
  static targets = ["grid", "title", "player"]

  connect() {
    this._lessonUpdateToken = 0
    this._onVideosUpdate = (event) => this.update(event)
    this.element.addEventListener("lessons--videos:update", this._onVideosUpdate)

    this._onVideosTabShown = (event) => {
      const trigger = event.target
      if (trigger?.getAttribute("href") !== "#tab-videos") return

      this.refreshCarouselLayout()
    }

    this._onVideosTabActive = () => {
      this.ensureCarousel()
    }

    const tabNav = document.getElementById("lesson-tablist-nav")
    tabNav?.addEventListener("shown.bs.tab", this._onVideosTabShown)
    this.element.addEventListener("lessons--videos:tab-active", this._onVideosTabActive)
  }

  disconnect() {
    this.element.removeEventListener("lessons--videos:update", this._onVideosUpdate)
    this.element.removeEventListener("lessons--videos:tab-active", this._onVideosTabActive)

    const tabNav = document.getElementById("lesson-tablist-nav")
    tabNav?.removeEventListener("shown.bs.tab", this._onVideosTabShown)

    if (this._carouselTimer) clearTimeout(this._carouselTimer)
    this.destroyCarousel()
  }

  update(event) {
    const detail = event?.detail || event
    const videos = lessonVideos(detail)
    const review_womb_component_lectures = Array.isArray(detail?.review_womb_component_lectures)
      ? detail.review_womb_component_lectures
      : []
    const updateToken = ++this._lessonUpdateToken

    this.currentLesson = detail

    if (!isLessonReleased(detail)) {
      if (this.titleElement()) this.titleElement().textContent = "Em breve..."
      this.renderList([], detail)
      this.renderListReviewWombComponentLectures([], detail)
      return
    }

    this.renderList(videos, detail)
    this.renderListReviewWombComponentLectures(review_womb_component_lectures, detail)

    if (videos.length) {
      this.ensureCarousel(updateToken)
    }
  }

  ensureCarousel(updateToken = this._lessonUpdateToken) {
    if (this._carouselTimer) clearTimeout(this._carouselTimer)

    const attempt = (tryCount = 0) => {
      if (updateToken !== this._lessonUpdateToken) return

      const grid = this.gridElement()
      const slideCount = grid?.querySelectorAll(".videos-carousel__slide").length || 0
      if (!slideCount) return

      if (this.isVideosPaneVisible() || tryCount >= 10) {
        try {
          this.initCarousel()
        } catch (error) {
          console.error("Erro ao iniciar carrossel de vídeos:", error)
        }
        return
      }

      this._carouselTimer = setTimeout(() => attempt(tryCount + 1), 100)
    }

    attempt(0)
  }

  isVideosPaneVisible() {
    const pane = document.getElementById("tab-videos")
    if (!pane) return false

    return pane.classList.contains("active")
      && pane.classList.contains("show")
      && window.getComputedStyle(pane).display !== "none"
  }

  refreshCarouselLayout() {
    const grid = this.gridElement()
    if (!grid || !window.jQuery) return

    const $grid = window.jQuery(grid)
    const hasSlides = $grid.find(".videos-carousel__slide").length > 0

    if (!hasSlides) return

    if ($grid.hasClass("slick-initialized")) {
      $grid.slick("setPosition")
      return
    }

    this.initCarousel()
  }

  titleElement() {
    return this.hasTitleTarget
      ? this.titleTarget
      : document.querySelector("[data-lessons--videos-target='title']")
  }

  playerElement() {
    return document.getElementById("now-lesson")
      || (this.hasPlayerTarget ? this.playerTarget : null)
  }

  videoSlideMarkup(video, index, lesson, isActive = false) {
    const watchedClass = video.attended ? "is-completed" : ""
    const activeClass = isActive ? " lesson-item--active" : ""

    return `
      <div class="videos-carousel__slide">
        <article id="video-${video.id}"
                 class="video-card${activeClass}"
                 data-title="${video.title}"
                 data-link="${video.link}"
                 data-attended="${video.attended}"
                 data-action="click->lessons--videos#play">
          <div class="video-card__thumb">
            <img class="video-card__img" src="${lesson.course_image}" alt="${video.title}">
            <span class="video-card__order">#${index + 1}</span>
          </div>

          <div class="video-card__body">
            <h6 class="video-card__title">${video.title}</h6>

            <div class="video-card__meta">
              <span class="badge ${video.attended ? "badge-success" : "badge-secondary"}">
                ${video.attended ? "Assistido" : "Não assistido"}
              </span>

              <button class="lesson-item__status"
                      type="button"
                      data-action="lessons--videos#toggle"
                      data-video-id="${video.id}">
                <span class="lesson-item__status-icon ${watchedClass}">
                  <i class="fa fa-check" aria-hidden="true"></i>
                </span>
              </button>
            </div>
          </div>
        </article>
      </div>
    `
  }

  initCarousel() {
    const grid = this.gridElement()
    if (!grid || !window.jQuery) return

    this.destroyCarousel()

    const slideCount = grid.querySelectorAll(".videos-carousel__slide").length
    if (!slideCount) return

    const desktopSlides = 4
    const mobileSlides = 2
    const $grid = window.jQuery(grid)

    $grid.slick({
      slidesToShow: desktopSlides,
      slidesToScroll: desktopSlides,
      infinite: false,
      arrows: slideCount > desktopSlides,
      dots: slideCount > desktopSlides,
      adaptiveHeight: false,
      nextArrow: '<button type="button" class="slick-arrow slick-next videos-carousel__arrow" aria-label="Próximos vídeos"><i class="fa-solid fa-chevron-right" aria-hidden="true"></i></button>',
      prevArrow: '<button type="button" class="slick-arrow slick-prev videos-carousel__arrow" aria-label="Vídeos anteriores"><i class="fa-solid fa-chevron-left" aria-hidden="true"></i></button>',
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: mobileSlides,
            slidesToScroll: mobileSlides,
            arrows: slideCount > mobileSlides,
            dots: slideCount > mobileSlides,
            adaptiveHeight: false,
          },
        },
      ],
    })

    if ($grid.hasClass("slick-initialized")) {
      $grid.slick("setPosition")
    }
  }

  destroyCarousel() {
    const grid = this.gridElement()
    if (!grid || !window.jQuery) return

    const $grid = window.jQuery(grid)

    if ($grid.hasClass("slick-initialized")) {
      try {
        $grid.slick("unslick")
      } catch (_error) {
        this.restoreCarouselSlides(grid)
      }
    }

    $grid.removeData("slick")
    grid.classList.remove("slick-initialized", "slick-slider")
    grid.querySelectorAll(".slick-track, .slick-list, .slick-arrow, .slick-dots").forEach((node) => node.remove())
  }

  restoreCarouselSlides(grid) {
    grid.querySelectorAll(".slick-slide").forEach((slide) => {
      const slideContent = slide.querySelector(".videos-carousel__slide") || slide.firstElementChild
      if (slideContent) grid.appendChild(slideContent)
    })
  }

  gridElement() {
    if (this.hasGridTarget) return this.gridTarget

    return this.element.querySelector("[data-lessons--videos-target='grid']")
  }

  renderList(videos, lesson) {
    const grid = this.gridElement()
    if (!grid) return

    this.destroyCarousel()
    grid.innerHTML = ""

    videos.forEach((video, index) => {
      grid.insertAdjacentHTML("beforeend", this.videoSlideMarkup(video, index, lesson, index === 0))
    })
  }

  renderListReviewWombComponentLectures(review_womb_component_lectures, lesson) {
    const grid = document.querySelector("[data-lessons-videos-review-target='grid']")
    if (!grid) return
    grid.innerHTML = ""

    review_womb_component_lectures.forEach((video, index) => {
      grid.insertAdjacentHTML(
        "beforeend",
        `
        <div class="col-lg-3 col-md-4 col-sm-6 mb-3">
          <article id="video-${video.id}"
                   class="video-card"
                   data-title="${video.title}"
                   data-link="${video.link}"
                   data-attended="${video.attended}"
                   data-action="click->lessons--videos#play">
            <div class="video-card__thumb">
              <img class="video-card__img" src="${lesson.course_image}" alt="${video.title}">
              <span class="video-card__order">#${index + 1}</span>
            </div>

            <div class="video-card__body">
              <h6 class="video-card__title">${video.title}</h6>
            </div>
          </article>
        </div>
        `
      )
    })
  }

  play(event, lesson = this.currentLesson) {
    const card = event.currentTarget.closest(".video-card")
    if (!card || !lesson) return

    const video = {
      id: card.id.split("-")[1],
      title: card.dataset.title,
      link: card.dataset.link,
      attended: card.dataset.attended === "true"
    }

    this.highlightCard(card)
    this.renderPlayer(video, lesson)
    this.dispatch("selected", { detail: video })
  }

  renderPlayer(video, lesson) {
    if (!video?.link) {
      this.resetPlayer(lesson)
      return
    }

    const title = this.titleElement()
    if (title) title.textContent = video.title

    const player = this.playerElement()
    if (!player) return

    renderVideoInto(player, video, lesson)
    this.renderCompletionButton(video.attended)
  }

  highlightCard(activeCard) {
    const cards = this.element.querySelectorAll(".video-card")
    cards.forEach((card) => card.classList.remove("active"))
    activeCard.classList.add("active")
  }

  renderCompletionButton(attended) {
    const area = document.querySelector(".area-btn-completed")
    const action = attended ? "lessons--progress#uncompleteVideo" : "lessons--progress#completeVideo"
    const text = attended ? "Desmarcar" : "Marcar"

    if (!area) return
    area.innerHTML = `
      <button class="btn btn-completed-video" data-action="${action}">
        ${text} como assistido
      </button>
    `
  }

  async toggle(event) {
    event.stopPropagation()

    const button = event.currentTarget
    const videoId = button.dataset.videoId
    const nowLesson = document.querySelector("#now-lesson")
    if (!nowLesson) return

    const lessonId = nowLesson.dataset.lesson.split("-")[1]
    const isCompleted = button.querySelector(".lesson-item__status-icon").classList.toggle("is-completed")
    const url = isCompleted
      ? `/lessons/${lessonId}/video/${videoId}/user_attended_video`
      : `/lessons/${lessonId}/video/${videoId}/user_not_attended_video`

    await fetch(url, { method: "GET" })

    const badge = button.closest(".video-card__meta").querySelector(".badge")
    badge.textContent = isCompleted ? "Assistido" : "Não assistido"
    badge.classList.toggle("badge-success", isCompleted)
    badge.classList.toggle("badge-secondary", !isCompleted)

    const card = document.querySelector(`#video-${videoId}`)
    if (card) card.dataset.attended = isCompleted

    this.dispatch("video-updated", { detail: { videoId, attended: isCompleted } })
  }

  resetPlayer(lesson) {
    const title = this.titleElement()
    if (title) title.textContent = lesson.title || ""

    const player = this.playerElement()
    if (!player) return

    player.setAttribute("data-lesson", `now-${lesson.id}`)
    player.removeAttribute("data-video")
    player.innerHTML = `
      <img class="img-fluid" alt="${lesson.course_title || "Aula"}"
           src="${lesson.course_image}"
           style="width: 100%;">
    `

    this.renderCompletionButton(lesson.attended)
  }
}
